import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ChevronDown, Truck, Users, ShoppingCart, Building,
  Activity, DollarSign, Briefcase, Factory, CheckCircle2, Car,
} from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const industries = [
  {
    id: 'logistics',
    icon: Truck,
    color: 'from-blue-500 to-cyan-500',
    glow: 'rgba(59,130,246,0.2)',
    title: 'Logistics & Transport',
    summary: 'Automate warehouse flows, freight booking and delivery tracking.',
    examples: [
      'Automatic order management and inventory control',
      'AI-based route optimisation for deliveries',
      'Real-time notifications to customers on status updates',
      'Integration with WMS, ERP and freight carrier APIs',
      'Automated customs declarations and documentation',
    ],
    savings: '14 hours/week',
  },
  {
    id: 'hr',
    icon: Users,
    color: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.2)',
    title: 'HR & Recruitment',
    summary: 'From CV screening to onboarding — fully automated.',
    examples: [
      'AI-driven CV screening and candidate ranking',
      'Automatic scheduling of interviews',
      'Digital onboarding with forms and approvals',
      'Ongoing reminders and communication with candidates',
      'Integration with HR systems (Teamtailor, Workday)',
    ],
    savings: '20 hours/week',
  },
  {
    id: 'ecommerce',
    icon: ShoppingCart,
    color: 'from-pink-500 to-rose-500',
    glow: 'rgba(236,72,153,0.2)',
    title: 'E-commerce & Retail',
    summary: 'Customer service, order flow and stock status — around the clock.',
    examples: [
      'AI chatbot for customer service and product questions',
      'Automatic order confirmation and return processing',
      'Product catalogue updates from supplier feeds',
      'Price optimisation based on stock and competitors',
      'Automated loyalty programmes and campaign emails',
    ],
    savings: '25 hours/week',
  },
  {
    id: 'property',
    icon: Building,
    color: 'from-amber-500 to-orange-500',
    glow: 'rgba(245,158,11,0.2)',
    title: 'Property & Construction',
    summary: 'Fault reports, tenancy agreements and project reports — automated.',
    examples: [
      'AI chatbot for tenant enquiries and fault reporting',
      'Automatic booking confirmation for viewings',
      'Generation of tenancy agreements and documentation',
      'Escalation of urgent cases to the right person',
      'Periodic reminders for inspections and maintenance',
    ],
    savings: '18 hours/week',
  },
  {
    id: 'health',
    icon: Activity,
    color: 'from-teal-500 to-emerald-500',
    glow: 'rgba(20,184,166,0.2)',
    title: 'Health & Life Science',
    summary: 'Secure patient flows and GDPR-compliant documentation.',
    examples: [
      'Automated patient booking and reminders',
      'AI forms for symptom collection and triage',
      'GDPR-compliant storage and management of medical records',
      'Automated report generation for authorities',
      'Integration with record systems (TakeCare, Cambio)',
    ],
    savings: '22 hours/week',
  },
  {
    id: 'finance',
    icon: DollarSign,
    color: 'from-emerald-500 to-green-500',
    glow: 'rgba(16,185,129,0.2)',
    title: 'Fintech & Finance',
    summary: 'Invoices, reports and compliance without manual handling.',
    examples: [
      'Automatic invoice processing and accounting',
      'AI-based credit assessment and risk analysis',
      'Periodised financial reports and year-end accounts',
      'Compliance reporting (AML, KYC)',
      'Integration with Fortnox, Visma and Bankgirot',
    ],
    savings: '30 hours/week',
  },
  {
    id: 'management',
    icon: Briefcase,
    color: 'from-indigo-500 to-violet-500',
    glow: 'rgba(99,102,241,0.2)',
    title: 'Management & Consulting',
    summary: 'Research, reporting and internal communication on autopilot.',
    examples: [
      'AI agents for research and market monitoring',
      'Automatic meeting notes and follow-ups',
      'Generation of client documents and presentations',
      'Internal knowledge base with AI search',
      'Automated status updates to clients',
    ],
    savings: '12 hours/week',
  },
  {
    id: 'automotive',
    icon: Car,
    color: 'from-slate-500 to-gray-600',
    glow: 'rgba(100,116,139,0.2)',
    title: 'Automotive & Tyres',
    summary: 'Automate seasonal booking, scheduling and customer communication.',
    examples: [
      'AI booking agent for tyre changes — available around the clock',
      'Intelligent scheduling based on stock and capacity',
      'Automatic booking confirmations and SMS reminders',
      'Integration with existing POS and business systems',
      'Escalation to staff for exceptions or complex cases',
    ],
    savings: '28 hours/week',
  },
  {
    id: 'manufacturing',
    icon: Factory,
    color: 'from-cyan-500 to-blue-500',
    glow: 'rgba(6,182,212,0.2)',
    title: 'Manufacturing & Industry',
    summary: 'Quality control, maintenance schedule and purchase orders — automated.',
    examples: [
      'Predictive maintenance based on sensor data',
      'Automatic purchase orders at low stock levels',
      'AI quality control with image analysis',
      'Digital production log and deviation management',
      'Integration with SCADA and MES systems',
    ],
    savings: '35 hours/week',
  },
];

const AccordionCard = ({
  item, isOpen, onToggle,
}: {
  item: typeof industries[0];
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
        isOpen ? 'border-indigo-200 shadow-lg' : 'border-border-light shadow-sm hover:border-indigo-100 hover:shadow-md'
      }`}
      onMouseEnter={e => { if (!isOpen) (e.currentTarget as HTMLElement).style.boxShadow = `0 10px 30px ${item.glow}`; }}
      onMouseLeave={e => { if (!isOpen) (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
    >
      <button className="w-full flex items-center gap-4 p-6 text-left" onClick={onToggle}>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0 shadow-md`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-bold text-text-primary mb-0.5">{item.title}</h3>
          <p className="text-[13px] text-text-secondary truncate">{item.summary}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="hidden sm:block text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
            ≈ {item.savings}
          </span>
          <ChevronDown className={`w-5 h-5 text-text-secondary transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-6 pb-6 border-t border-border-light pt-5">
              <p className="text-sm font-semibold text-text-secondary mb-3">What we automate for you:</p>
              <ul className="space-y-2.5">
                {item.examples.map(ex => (
                  <li key={ex} className="flex items-start gap-2.5 text-[14px] text-text-secondary">
                    <CheckCircle2 className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    {ex}
                  </li>
                ))}
              </ul>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-start gap-2 bg-red-50 rounded-xl border border-red-100 px-4 py-3">
                  <span className="text-xs font-bold text-red-500 uppercase tracking-wide shrink-0 mt-0.5">Before</span>
                  <span className="text-xs text-text-secondary leading-relaxed">Manual handling — time that disappears every week</span>
                </div>
                <div className="flex items-start gap-2 bg-emerald-50 rounded-xl border border-emerald-100 px-4 py-3">
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide shrink-0 mt-0.5">After</span>
                  <span className="text-xs text-text-secondary leading-relaxed">Automated flow — nothing falls through the cracks</span>
                </div>
              </div>
              <div className="mt-3 pt-4 border-t border-border-light flex items-center justify-between">
                <span className="text-sm text-text-secondary">
                  Time saved: <span className="font-bold text-text-primary">{item.savings}</span>
                  <span className="text-emerald-600 font-semibold ml-2">≈ €1 000–5 000/month</span>
                </span>
                <Link to="/en/contact">
                  <button className="text-sm font-semibold text-brand-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors">
                    Send a request <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const EnAutomationsPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openId, setOpenId] = useState<string | null>('logistics');
  const toggle = (id: string) => setOpenId(prev => prev === id ? null : id);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      <LightPageHero
        badge="AI AUTOMATION"
        heading={<>This is what you can stop doing<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>manually this year</span></>}
        subtext="Click on your industry and see exactly what we remove from the to-do list."
      />

      {/* ── Examples of what we build ── */}
      <section className="py-14 bg-surface-subtle border-b border-border-light">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-6 text-center"
            >
              Examples of what we actually build
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { from: 'Email',    to: 'automatic responses and case status' },
                { from: 'Bookings', to: 'straight into systems without manual handling' },
                { from: 'Excel',    to: 'automatic dashboards and reports' },
                { from: 'Support',  to: 'chatbot that responds 24/7' },
              ].map(({ from, to }) => (
                <motion.div
                  key={from}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-3 bg-white rounded-xl border border-border-light px-5 py-4 shadow-sm"
                >
                  <span className="text-sm font-bold text-text-primary min-w-[80px]">{from}</span>
                  <span className="text-text-muted text-sm font-semibold">→</span>
                  <span className="text-sm text-text-secondary">{to}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── What you get ── */}
      <section className="py-14 bg-white border-b border-border-light">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-6 text-center"
            >
              What you get
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { title: 'Fewer manual tasks',        desc: 'Routine work that repeats daily or weekly is handled automatically.' },
                { title: 'Faster responses to customers', desc: 'Automatic replies, confirmations and reminders — without delay.' },
                { title: 'Less administration',       desc: 'Reports, updates and documentation are generated without manual work.' },
              ].map(({ title, desc }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-surface-subtle rounded-xl border border-border-light p-5"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mb-3" />
                  <p className="text-[15px] font-bold text-text-primary mb-1">{title}</p>
                  <p className="text-[13px] text-text-secondary leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Main: accordion + sidebar ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">

            <div className="lg:col-span-2 space-y-3">
              {industries.map(item => (
                <AccordionCard
                  key={item.id}
                  item={item}
                  isOpen={openId === item.id}
                  onToggle={() => toggle(item.id)}
                />
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-5">
                <div className="bg-surface-subtle rounded-2xl border border-border-light p-6">
                  <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">Why Aifinity</p>
                  <div className="space-y-4">
                    {[
                      { icon: '→', label: 'Fast prototype — see results before you pay', color: 'from-indigo-500 to-violet-600' },
                      { icon: '→', label: 'Integration with systems you already have', color: 'from-emerald-400 to-teal-500' },
                      { icon: '→', label: 'Ongoing support after launch', color: 'from-amber-400 to-orange-500' },
                    ].map(s => (
                      <div key={s.label} className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center shrink-0 mt-0.5`}>
                          <span className="text-white text-[10px] font-bold">{s.icon}</span>
                        </div>
                        <p className="text-[13px] text-text-secondary leading-snug">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 rounded-2xl p-6 text-white">
                  <p className="text-sm font-bold mb-2">Ready to automate?</p>
                  <p className="text-sm text-white/70 leading-relaxed mb-5">
                    Tell us about a problem that takes time every week. We'll analyse it for free and let you know if we can solve it — usually within 2 weeks.
                  </p>
                  <Link to="/en/contact">
                    <button className="w-full bg-white text-indigo-700 font-semibold text-sm py-3 rounded-xl hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2 group">
                      Describe your needs
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </Link>
                </div>

                <p className="text-[12px] text-text-secondary text-center leading-relaxed px-2">
                  Response within 24 hours. We'll tell you if we can help — or not.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Don't see your industry?
            </h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto">
              We build custom automation solutions for all types of B2B companies. Get in touch and tell us about your processes.
            </p>
            <Link to="/en/contact">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
                Describe your needs
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
            <p className="text-xs text-text-muted mt-3">Response within 24 hours · You get a concrete proposal</p>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default EnAutomationsPage;
